import { nonEmptyString } from '../lib/format';
import { logger } from '../lib/logger';
import { useEffect, useState } from 'react';
import type { CustomFieldPreset, FactTableColumnTemplate, LegalCitationFormatId, Preferences } from '../types';
import { API_URL, apiFetch, jsonInit } from '../lib/api';
import type { TabId } from '../components/layout/AppSidebar';

export interface UsePreferencesDraftOptions {
  preferences: Preferences | null;
  setPreferences: (prefs: Preferences | null) => void;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
  notify: (message: string) => void;
  showAlert: (title: string, message: string) => void;
}

const CLEAR_DATA_CONFIRM_PHRASE = 'DELETE ALL DATA';

// Owns every piece of App.tsx's Preferences-tab draft/edit state (~25
// fields), dirty tracking, the nav-away guard (tryNavigate/confirm/discard/
// cancel), and the danger-zone clear-all-data flow. Resolves the duplication
// between the old handleSavePreferences and handleConfirmSaveAndContinue,
// which built the same updatedPrefs object and ran the same PUT/toast/error
// sequence — both now funnel through save().
export function usePreferencesDraft({ preferences, setPreferences, activeTab, setActiveTab, notify, showAlert }: UsePreferencesDraftOptions): UsePreferencesDraftResult {
  const [archetype, setArchetype] = useState<Preferences['archetype']>('balanced-civil');
  const [defaultLens, setDefaultLens] = useState<Preferences['default_lens']>('ladder');
  const [tierLabels, setTierLabels] = useState<Record<string, string>>({});
  const [guidanceNarrative, setGuidanceNarrative] = useState(false);
  const [guidanceConfiguration, setGuidanceConfiguration] = useState(false);
  const [navOrder, setNavOrder] = useState<string[]>([]);
  const [barLicenses, setBarLicenses] = useState<Record<string, Record<string, string>>>({});
  const [licenseAttorneyId, setLicenseAttorneyId] = useState('');
  const [licenseStateCode, setLicenseStateCode] = useState('');
  const [licenseBarNumber, setLicenseBarNumber] = useState('');
  const [researchProvider, setResearchProvider] = useState('');
  const [citationFormat, setCitationFormat] = useState<LegalCitationFormatId>('bluebook');

  const [brandingUseCustom, setBrandingUseCustom] = useState(false);
  const [brandingFirmName, setBrandingFirmName] = useState('');
  const [brandingPrimaryColor, setBrandingPrimaryColor] = useState('#2557d6');
  const [brandingSecondaryColor, setBrandingSecondaryColor] = useState('#1b2a4a');
  const [brandingLogoText, setBrandingLogoText] = useState('');
  const [brandingLogoImage, setBrandingLogoImage] = useState('');
  const [logoUploadError, setLogoUploadError] = useState('');

  const [activeSubTab, setActiveSubTab] = useState<'calibration' | 'ui' | 'wording' | 'guidance' | 'bar-licenses' | 'research' | 'branding' | 'motions' | 'fact-presets' | 'danger-zone'>('calibration');

  const [pendingTab, setPendingTab] = useState<TabId | null>(null);
  const [pendingTabAction, setPendingTabAction] = useState<(() => void) | null>(null);

  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [clearDataConfirmText, setClearDataConfirmText] = useState('');
  const [clearingData, setClearingData] = useState(false);
  const [clearDataError, setClearDataError] = useState<string | null>(null);

  const updateSavedPreferences = (prefs: Preferences): void => {
    setPreferences(prefs);
    if (prefs) {
      setArchetype(prefs.archetype);
      setDefaultLens(prefs.default_lens);
      setTierLabels((prefs.tier_labels ?? {}));
      setGuidanceNarrative((prefs.guidance_dismissed?.narrative ?? false));
      setGuidanceConfiguration((prefs.guidance_dismissed?.configuration ?? false));
      setNavOrder((prefs.nav_order ?? ['stages', 'float', 'claims', 'actions', 'triggers', 'aud']));
      setBarLicenses((prefs.bar_licenses ?? {}));
      setResearchProvider((prefs.research_provider ?? ''));
      setCitationFormat(prefs.citation_format ?? 'bluebook');
      setBrandingUseCustom((prefs.branding?.use_custom_branding ?? false));
      setBrandingFirmName((prefs.branding?.law_firm_name ?? ''));
      setBrandingPrimaryColor((nonEmptyString(prefs.branding?.primary_color) ?? '#2557d6'));
      setBrandingSecondaryColor((nonEmptyString(prefs.branding?.secondary_color) ?? '#1b2a4a'));
      setBrandingLogoText((prefs.branding?.logo_text ?? ''));
      setBrandingLogoImage((prefs.branding?.logo_image ?? ''));
    }
  };

  const isDirty = !!(preferences && (
    archetype !== preferences.archetype ||
    defaultLens !== preferences.default_lens ||
    guidanceNarrative !== ((preferences.guidance_dismissed?.narrative ?? false)) ||
    guidanceConfiguration !== ((preferences.guidance_dismissed?.configuration ?? false)) ||
    JSON.stringify(tierLabels) !== JSON.stringify(preferences.tier_labels) ||
    JSON.stringify(navOrder) !== JSON.stringify(preferences.nav_order) ||
    JSON.stringify(barLicenses) !== JSON.stringify((preferences.bar_licenses ?? {})) ||
    researchProvider !== ((preferences.research_provider ?? '')) ||
    citationFormat !== (preferences.citation_format ?? 'bluebook') ||
    brandingUseCustom !== ((preferences.branding?.use_custom_branding ?? false)) ||
    brandingFirmName !== ((preferences.branding?.law_firm_name ?? '')) ||
    brandingPrimaryColor !== ((nonEmptyString(preferences.branding?.primary_color) ?? '#2557d6')) ||
    brandingSecondaryColor !== ((nonEmptyString(preferences.branding?.secondary_color) ?? '#1b2a4a')) ||
    brandingLogoText !== ((preferences.branding?.logo_text ?? '')) ||
    brandingLogoImage !== ((preferences.branding?.logo_image ?? ''))
  ));

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes in your preferences. Are you sure you want to leave?';

      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const buildUpdatedPrefs = (): Preferences => ({
    ...preferences,
    type: 'prefs',
    id: (nonEmptyString(preferences?.id) ?? 'user-default'),
    archetype,
    default_lens: defaultLens,
    tier_labels: tierLabels,
    guidance_dismissed: {
      narrative: guidanceNarrative,
      configuration: guidanceConfiguration,
    },
    nav_order: navOrder,
    bar_licenses: barLicenses,
    research_provider: researchProvider,
    citation_format: citationFormat,
    branding: {
      use_custom_branding: brandingUseCustom,
      law_firm_name: brandingFirmName,
      primary_color: brandingPrimaryColor,
      secondary_color: brandingSecondaryColor,
      logo_text: brandingLogoText,
      logo_image: brandingLogoImage,
    },
  });

  // Single implementation used by both the Preferences tab's "Save Settings"
  // button and the "Save & Continue" nav-guard confirmation.
  const saveToServer = async (): Promise<boolean> => {
    const updatedPrefs = buildUpdatedPrefs();
    try {
      const res = await apiFetch(`/api/v1/config/preferences`, jsonInit('PUT', updatedPrefs));
      if (res.ok) {
        updateSavedPreferences(updatedPrefs);
        notify('Preferences saved successfully!');
        return true;
      }
      showAlert('Error', 'Failed to save preferences.');
      return false;
    } catch (err) {
      logger.error(err);
      showAlert('Error', 'Failed to save preferences.');
      return false;
    }
  };

  const save = async (): Promise<void> => {
    await saveToServer();
  };

  const discard = (): void => {
    if (preferences) {
      setArchetype(preferences.archetype);
      setDefaultLens(preferences.default_lens);
      setTierLabels((preferences.tier_labels ?? {}));
      setGuidanceNarrative((preferences.guidance_dismissed?.narrative ?? false));
      setGuidanceConfiguration((preferences.guidance_dismissed?.configuration ?? false));
      setNavOrder((preferences.nav_order ?? []));
      setResearchProvider((preferences.research_provider ?? ''));
      setCitationFormat(preferences.citation_format ?? 'bluebook');
    }
  };

  const saveMotionWorkflows = async (next: { us: import('../types').MotionWorkflowTemplate; other_side: import('../types').MotionWorkflowTemplate }): Promise<void> => {
    if (!preferences) return;
    const updatedPrefs: Preferences = { ...preferences, motion_workflows: next };
    try {
      const res = await apiFetch(`/api/v1/config/preferences`, jsonInit('PUT', updatedPrefs));
      if (res.ok) {
        setPreferences(updatedPrefs);
        notify('Motion workflow steps saved!');
      }
    } catch (err) {
      logger.error('Failed to save motion workflows:', err);
    }
  };

  // Preset edits are saved immediately because the same library is available
  // from both the Facts page and Preferences. This keeps both entry points in
  // sync and avoids losing a modal edit when the user navigates away.
  const saveCustomFieldPresets = async (next: CustomFieldPreset[]): Promise<boolean> => {
    if (!preferences) {
      showAlert('Error', 'Preferences are still loading. Please try again.');
      return false;
    }
    const updatedPrefs: Preferences = { ...preferences, custom_field_presets: next };
    try {
      const res = await apiFetch(`/api/v1/config/preferences`, jsonInit('PUT', updatedPrefs));
      if (!res.ok) {
        showAlert('Error', 'Failed to save the custom field preset library.');
        return false;
      }
      updateSavedPreferences(updatedPrefs);
      notify('Custom field preset library saved.');
      return true;
    } catch (err) {
      logger.error('Failed to save custom field presets:', err);
      showAlert('Error', 'Failed to save the custom field preset library.');
      return false;
    }
  };

  const saveFactTableTemplates = async (next: FactTableColumnTemplate[]): Promise<boolean> => {
    if (!preferences) {
      showAlert('Error', 'Preferences are still loading. Please try again.');
      return false;
    }
    const updatedPrefs: Preferences = { ...preferences, fact_table_column_templates: next };
    try {
      const res = await apiFetch(`/api/v1/config/preferences`, jsonInit('PUT', updatedPrefs));
      if (!res.ok) {
        showAlert('Error', 'Failed to save the fact table template library.');
        return false;
      }
      updateSavedPreferences(updatedPrefs);
      notify('Fact table templates saved.');
      return true;
    } catch (err) {
      logger.error('Failed to save fact table templates:', err);
      showAlert('Error', 'Failed to save the fact table template library.');
      return false;
    }
  };

  const moveNavOrder = (index: number, direction: 'up' | 'down'): void => {
    const newOrder = [...navOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newOrder.length) {
      const temp = newOrder[index];
      const nextItem = newOrder[targetIndex];
      if (temp !== undefined && nextItem !== undefined) {
        newOrder[index] = nextItem;
        newOrder[targetIndex] = temp;
        setNavOrder(newOrder);
      }
    }
  };

  const handleLogoFileSelected = (file: File | null): void => {
    setLogoUploadError('');
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|svg\+xml|webp)$/.test(file.type)) {
      setLogoUploadError('Please choose a PNG, JPG, WEBP, or SVG image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoUploadError('Logo image must be smaller than 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (): void => {
      const dataUrl = reader.result as string;
      if (file.type === 'image/svg+xml') {
        setBrandingLogoImage(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = (): void => {
        const maxDim = 480;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setBrandingLogoImage(dataUrl);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setBrandingLogoImage(canvas.toDataURL('image/png'));
      };
      img.onerror = (): void => setLogoUploadError('Could not read that image file.');
      img.src = dataUrl;
    };
    reader.onerror = (): void => setLogoUploadError('Could not read that image file.');
    reader.readAsDataURL(file);
  };

  const tryNavigate = (target: TabId, action?: () => void): void => {
    if (activeTab === 'preferences' && isDirty && target !== 'preferences') {
      setPendingTab(target);
      setPendingTabAction(() => action ?? null);
    } else {
      if (action) action();
      setActiveTab(target);
    }
  };

  const confirmSaveAndContinue = async (): Promise<void> => {
    if (!await saveToServer()) return;
    if (pendingTab) {
      if (pendingTabAction) pendingTabAction();
      setActiveTab(pendingTab);
    }
    setPendingTab(null);
    setPendingTabAction(null);
  };

  const confirmDiscardAndContinue = (): void => {
    discard();
    if (pendingTab) {
      if (pendingTabAction) pendingTabAction();
      setActiveTab(pendingTab);
    }
    setPendingTab(null);
    setPendingTabAction(null);
  };

  const confirmCancel = (): void => {
    setPendingTab(null);
    setPendingTabAction(null);
  };

  const openClearDataModal = (): void => {
    setClearDataConfirmText('');
    setClearDataError(null);
    setShowClearDataModal(true);
  };

  const handleClearAllData = async (): Promise<void> => {
    if (clearDataConfirmText !== CLEAR_DATA_CONFIRM_PHRASE) return;
    setClearingData(true);
    setClearDataError(null);
    try {
      const res = await apiFetch(`${API_URL}/api/v1/account/all-data`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: clearDataConfirmText }),
      });
      if (!res.ok) {
        const data = await res.json<{message?: string}>().catch(() => null);
        setClearDataError((nonEmptyString(data?.message) ?? 'Failed to clear data.'));
        setClearingData(false);
        return;
      }
      window.location.reload();
    } catch {
      setClearDataError('Failed to clear data.');
      setClearingData(false);
    }
  };

  return {
    archetype, setArchetype,
    defaultLens, setDefaultLens,
    tierLabels, setTierLabels,
    guidanceNarrative, setGuidanceNarrative,
    guidanceConfiguration, setGuidanceConfiguration,
    navOrder, moveNavOrder,
    barLicenses, setBarLicenses,
    researchProvider, setResearchProvider,
    citationFormat, setCitationFormat,
    licenseAttorneyId, setLicenseAttorneyId,
    licenseStateCode, setLicenseStateCode,
    licenseBarNumber, setLicenseBarNumber,
    brandingUseCustom, setBrandingUseCustom,
    brandingFirmName, setBrandingFirmName,
    brandingPrimaryColor, setBrandingPrimaryColor,
    brandingSecondaryColor, setBrandingSecondaryColor,
    brandingLogoText, setBrandingLogoText,
    brandingLogoImage, setBrandingLogoImage,
    logoUploadError, handleLogoFileSelected,
    activeSubTab, setActiveSubTab,
    isDirty, save, discard, saveMotionWorkflows, saveCustomFieldPresets, saveFactTableTemplates,
    updateSavedPreferences,
    tryNavigate,
    pendingTab, confirmSaveAndContinue, confirmDiscardAndContinue, confirmCancel,
    showClearDataModal, openClearDataModal, closeClearDataModal: () => setShowClearDataModal(false),
    clearDataConfirmText, setClearDataConfirmText,
    clearingData, clearDataError, handleClearAllData,
    CLEAR_DATA_CONFIRM_PHRASE,
  };
}

export interface UsePreferencesDraftResult {
    archetype: "balanced-civil" | "documentarian" | "trial-lawyer" | "motions-practitioner";
    setArchetype: import("react").Dispatch<import("react").SetStateAction<"balanced-civil" | "documentarian" | "trial-lawyer" | "motions-practitioner">>;
    defaultLens: "ladder" | "battlefield" | "split";
    setDefaultLens: import("react").Dispatch<import("react").SetStateAction<"ladder" | "battlefield" | "split">>;
    tierLabels: Record<string, string>;
    setTierLabels: import("react").Dispatch<import("react").SetStateAction<Record<string, string>>>;
    guidanceNarrative: boolean;
    setGuidanceNarrative: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    guidanceConfiguration: boolean;
    setGuidanceConfiguration: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    navOrder: string[];
    moveNavOrder: (index: number, direction: "up" | "down") => void;
    barLicenses: Record<string, Record<string, string>>;
    setBarLicenses: import("react").Dispatch<import("react").SetStateAction<Record<string, Record<string, string>>>>;
    researchProvider: string;
    setResearchProvider: import("react").Dispatch<import("react").SetStateAction<string>>;
    citationFormat: LegalCitationFormatId;
    setCitationFormat: import("react").Dispatch<import("react").SetStateAction<LegalCitationFormatId>>;
    licenseAttorneyId: string;
    setLicenseAttorneyId: import("react").Dispatch<import("react").SetStateAction<string>>;
    licenseStateCode: string;
    setLicenseStateCode: import("react").Dispatch<import("react").SetStateAction<string>>;
    licenseBarNumber: string;
    setLicenseBarNumber: import("react").Dispatch<import("react").SetStateAction<string>>;
    brandingUseCustom: boolean;
    setBrandingUseCustom: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    brandingFirmName: string;
    setBrandingFirmName: import("react").Dispatch<import("react").SetStateAction<string>>;
    brandingPrimaryColor: string;
    setBrandingPrimaryColor: import("react").Dispatch<import("react").SetStateAction<string>>;
    brandingSecondaryColor: string;
    setBrandingSecondaryColor: import("react").Dispatch<import("react").SetStateAction<string>>;
    brandingLogoText: string;
    setBrandingLogoText: import("react").Dispatch<import("react").SetStateAction<string>>;
    brandingLogoImage: string;
    setBrandingLogoImage: import("react").Dispatch<import("react").SetStateAction<string>>;
    logoUploadError: string;
    handleLogoFileSelected: (file: File | null) => void;
    activeSubTab: "calibration" | "ui" | "wording" | "guidance" | "bar-licenses" | "research" | "branding" | "motions" | "fact-presets" | "danger-zone";
    setActiveSubTab: import("react").Dispatch<import("react").SetStateAction<"calibration" | "ui" | "wording" | "guidance" | "bar-licenses" | "research" | "branding" | "motions" | "fact-presets" | "danger-zone">>;
    isDirty: boolean;
    save: () => Promise<void>;
    discard: () => void;
    saveMotionWorkflows: (next: {
        us: import("../types").MotionWorkflowTemplate;
        other_side: import("../types").MotionWorkflowTemplate;
    }) => Promise<void>;
    saveCustomFieldPresets: (next: CustomFieldPreset[]) => Promise<boolean>;
    saveFactTableTemplates: (next: FactTableColumnTemplate[]) => Promise<boolean>;
    updateSavedPreferences: (prefs: Preferences) => void;
    tryNavigate: (target: TabId, action?: () => void) => void;
    pendingTab: TabId | null;
    confirmSaveAndContinue: () => Promise<void>;
    confirmDiscardAndContinue: () => void;
    confirmCancel: () => void;
    showClearDataModal: boolean;
    openClearDataModal: () => void;
    closeClearDataModal: () => void;
    clearDataConfirmText: string;
    setClearDataConfirmText: import("react").Dispatch<import("react").SetStateAction<string>>;
    clearingData: boolean;
    clearDataError: string | null;
    handleClearAllData: () => Promise<void>;
    CLEAR_DATA_CONFIRM_PHRASE: string;
}

